import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../services/api';

export function useAnalytics(projectId: string | undefined) {
  const queryClient = useQueryClient();

  // Get analytics overview
  const useOverview = (dateRange: { from: Date; to: Date }) => {
    // Create stable query key using date strings instead of Date objects
    const startDate = dateRange.from.toISOString().split('T')[0];
    const endDate = dateRange.to.toISOString().split('T')[0];
    
    return useQuery({
      queryKey: ['analytics', 'overview', projectId, startDate, endDate],
      queryFn: () => analyticsApi.getOverview(
        projectId as string, 
        { startDate, endDate }
      ),
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    });
  };

  // Get realtime analytics
  const useRealtime = () => {
    return useQuery({
      queryKey: ['analytics', 'realtime', projectId],
      queryFn: () => analyticsApi.getRealtime(projectId as string),
      enabled: !!projectId,
      refetchInterval: 30 * 1000, // Refetch every 30 seconds
    });
  };

  // Get traffic analytics
  const useTraffic = (dateRange: { from: Date; to: Date }) => {
    // Create stable query key using date strings instead of Date objects
    const startDate = dateRange.from.toISOString().split('T')[0];
    const endDate = dateRange.to.toISOString().split('T')[0];
    
    return useQuery({
      queryKey: ['analytics', 'traffic', projectId, startDate, endDate],
      queryFn: () => analyticsApi.getTraffic(
        projectId as string, 
        { startDate, endDate }
      ),
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    });
  };

  // Get audience analytics
  const useAudience = (dateRange: { from: Date; to: Date }) => {
    // Create stable query key using date strings instead of Date objects
    const startDate = dateRange.from.toISOString().split('T')[0];
    const endDate = dateRange.to.toISOString().split('T')[0];
    
    return useQuery({
      queryKey: ['analytics', 'audience', projectId, startDate, endDate],
      queryFn: () => analyticsApi.getAudience(
        projectId as string, 
        { startDate, endDate }
      ),
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    });
  };

  // Get conversion analytics
  const useConversions = (dateRange: { from: Date; to: Date }) => {
    // Create stable query key using date strings instead of Date objects
    const startDate = dateRange.from.toISOString().split('T')[0];
    const endDate = dateRange.to.toISOString().split('T')[0];
    
    return useQuery({
      queryKey: ['analytics', 'conversions', projectId, startDate, endDate],
      queryFn: () => analyticsApi.getConversions(
        projectId as string, 
        { startDate, endDate }
      ),
      enabled: !!projectId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      retryDelay: 1000,
    });
  };

  // GA4 Audience Export hooks
  const useListAudiences = () => {
    return useQuery({
      queryKey: ['analytics', 'audiences', 'list', projectId],
      queryFn: () => analyticsApi.listAudiences(projectId as string),
      enabled: !!projectId,
      staleTime: 30 * 60 * 1000, // 30 minutes - audiences don't change often
    });
  };

  const useCreateAudienceExport = () => {
    return useMutation({
      mutationFn: ({ audienceId, dimensions }: { audienceId: string; dimensions?: string[] }) =>
        analyticsApi.createAudienceExport(projectId as string, audienceId, dimensions),
      onSuccess: () => {
        // Invalidate audience export queries to refresh status
        queryClient.invalidateQueries({ queryKey: ['analytics', 'audience-exports', projectId] });
      },
    });
  };

  const useAudienceExportStatus = (exportId: string | undefined) => {
    return useQuery({
      queryKey: ['analytics', 'audience-exports', projectId, exportId, 'status'],
      queryFn: () => analyticsApi.checkAudienceExportStatus(projectId as string, exportId as string),
      enabled: !!projectId && !!exportId,
      refetchInterval: (data: any) => {
        // Poll every 5 seconds if export is still creating, otherwise don't poll
        return data?.status?.state === 'CREATING' ? 5000 : false;
      },
    });
  };

  const useAudienceExportData = (exportId: string | undefined, audienceId: string | undefined, enabled: boolean = true) => {
    return useQuery({
      queryKey: ['analytics', 'audience-exports', projectId, exportId, audienceId, 'data'],
      queryFn: () => analyticsApi.queryAudienceExportData(projectId as string, exportId as string, audienceId),
      enabled: !!projectId && !!exportId && enabled,
      staleTime: 60 * 60 * 1000, // 1 hour - export data is static once created
    });
  };

  return {
    useOverview,
    useRealtime,
    useTraffic,
    useAudience,
    useConversions,
    useListAudiences,
    useCreateAudienceExport,
    useAudienceExportStatus,
    useAudienceExportData,
  };
}